import { useEffect, useState } from 'react'
import { formatterPrice } from 'src/utils/formatterPrice'
import { makeAddToCartEvent, sendEvent } from 'src/utils/restructure/analytics'
import { useMobile } from 'src/hooks/useMobile'
import { Image } from 'src/components/ui/Image'
import { useDebounce } from 'src/hooks/useDebounce'
import { AddToCartIcon } from 'src/components/Icons/AddToCartIcon'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { isPreSale } from 'src/utils/pdp/isPreSale'

import { useProductContextSelector } from '../../contexts'
import { useBuyButton } from '../Sidebar/ProductAction/BuyButton/hooks/useBuyButton'
import { publish } from '../../productEvents'
import { BuyButton } from '../Sidebar/ProductAction/BuyButton'
import { SkuSelector } from '../Sidebar/SkuSelector'
import './styles.scss'

const isPartiallyVisibleInViewport = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect()

  return (
    rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
    rect.bottom > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
    rect.right > 0
  )
}

const handleScroll = ({
  setIsVisible,
  isMobile = false,
}: {
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  isMobile?: boolean
}) => {
  const header = document.querySelector<HTMLDivElement>(
    '[data-component="header-container"]'
  )

  const buyButtonArea = document.querySelector<HTMLDivElement>(
    '[data-component="product-action"]'
  )

  const buyButtonPosition = buyButtonArea
    ? buyButtonArea.getBoundingClientRect().top < 0
    : false

  const isBuyButtonVisible =
    !!buyButtonArea && isPartiallyVisibleInViewport(buyButtonArea)

  const isHeaderPartiallyVisible =
    !!header && isPartiallyVisibleInViewport(header)

  const stickyBarVisibilityConditions = isMobile
    ? buyButtonPosition && !isBuyButtonVisible
    : !isBuyButtonVisible && !isHeaderPartiallyVisible

  setIsVisible(stickyBarVisibilityConditions)
}

export function StickyBuyBar() {
  const currentSku = useProductContextSelector((ctx) => ctx.currentSku)
  const product = useProductContextSelector((ctx) => ctx.product)
  const deliveryIsAvailable = useProductContextSelector(
    (ctx) => ctx.deliveryIsAvailable
  )

  const {
    orderForm: { shippingData },
  } = useCheckout()

  const { isAvailable } = currentSku

  const [isVisible, setIsVisible] = useState(false)
  const { addToCart } = useBuyButton()
  const { isMobile } = useMobile()
  const debouncedHandleScroll = useDebounce(
    () => handleScroll({ setIsVisible, isMobile }),
    100
  )

  const {
    sellerDefault: { sellerOffer },
    images,
    name,
  } = currentSku

  const discountAmount = formatterPrice(sellerOffer.discount).replace(/\s/g, '')

  const handleBuyButton = () => {
    publish('addToCart')

    addToCart({
      item: {
        id: currentSku.itemId,
        productId: product.productId,
        isPreSale: isPreSale(currentSku.estimatedDateArrival),
        quantity: 1,
        seller: currentSku.sellerDefault.sellerId,
        index: 0,
        price: currentSku.offerDefault?.price ?? 0,
      },
    })

    const addToCartEvent = makeAddToCartEvent([
      { product, productItem: currentSku },
    ])

    sendEvent(addToCartEvent)
  }

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll)

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll)
    }
  }, [isMobile, setIsVisible, debouncedHandleScroll])

  const showStickyBuyBar =
    isAvailable && (!shippingData?.address?.postalCode || deliveryIsAvailable)

  if (!isMobile) {
    return (
      <div
        className={`sticky-bar flex justify-between gap-6 fixed top-0 z-40 w-full border-b border-gray bg-restructure-background-primary py-4 px-6
           ${
             isVisible && showStickyBuyBar
               ? 'sticky-bar--visible'
               : 'sticky-bar--hidden'
           }`}
        aria-hidden={!isVisible}
      >
        <div className="flex gap-6 items-center">
          <div className="bg-restructure-background-neutral-3">
            <Image
              className="mix-blend-multiply"
              src={images[0].imageUrl}
              width={88}
              height={88}
              alt={images[0].imageText}
              loading="lazy"
              data-testid="image-floating-button"
            />
          </div>
          <div className="flex flex-col gap-4">
            <p
              data-testid="stickybar-product-name"
              className="text-font-size-4 leading-none font-semibold text-restructure-primary"
            >
              {name}
            </p>
            <div className="flex gap-2 items-center">
              {sellerOffer.hasDiscount && (
                <p className="text-font-size-3 font-medium text-tertiary line-through">
                  {formatterPrice(sellerOffer.listPrice)}
                </p>
              )}
              <p className="text-font-size-4 leading-none font-semibold text-restructure-primary">
                {formatterPrice(
                  sellerOffer.cashDiscountPercentage
                    ? sellerOffer.cashPrice
                    : sellerOffer.price
                )}
              </p>
              {sellerOffer.hasDiscount && (
                <p className="w-fit py-[2px] px-1 bg-redDiscount text-font-size-2 font-medium text-restructure-primary-inverted">
                  -{discountAmount}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <SkuSelector
            isFromStickyBar
            classContainer="!flex-row gap-4 !pt-0 !border-none"
            hasDisabledButtons={!isVisible}
          />
          <div className="flex w-[270px]">
            <BuyButton
              quantity={1}
              isVisible={isVisible}
              loaderVariant="white"
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`sticky-bar flex flex-col z-40 fixed w-full border-t border-gray ${
        isVisible && showStickyBuyBar
          ? 'sticky-bar--visible'
          : 'sticky-bar--hidden'
      } bottom-0`}
      aria-hidden={!isVisible}
    >
      <button
        onClick={handleBuyButton}
        className="flex justify-between py-6 px-4 bg-restructure-background-action-1"
      >
        <div className="flex gap-2 items-center">
          <AddToCartIcon />
          <p className="text-restructure-primary-inverted text-font-size-4 leading-[21px] font-medium">
            Adicionar ao carrinho
          </p>
        </div>
        <p className="text-restructure-primary-inverted text-font-size-4 leading-[21px] font-medium">
          {formatterPrice(
            sellerOffer.cashDiscountPercentage
              ? sellerOffer.cashPrice
              : sellerOffer.price
          )}
        </p>
      </button>
    </div>
  )
}
